import { useState, useMemo } from 'react'
import { MapContainer, useMap } from 'react-leaflet'
import { Box } from '@mui/material'
import Leaflet from 'leaflet'
// @ts-ignore
import MarkerClusterGroup from 'react-leaflet-markercluster'
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer'
import GestureHandling from 'leaflet-gesture-handling'
import { composePlacesBounds } from '../../../local/utils'
import LeafletMapPlaces from './LeafletMapPlaces'
import { getDrupalSetting } from '@ggs/utils'
import { useWindowResize } from '@ggs/hooks'
import { Loader } from '../../indicators'
import './LeafletMap.scss'

/**
 * @typedef {import('../../../definitions').LeafletPlace} LeafletPlace
 * @typedef {import('../../../definitions').MarkerCluster} MarkerCluster
 * @typedef {import('react-leaflet').MapContainerProps} MapContainerProps
 */

/**
 * @typedef {import('leaflet').DivIcon} DivIcon
 */

/**
 * Create custom Leaflet cluster icon.
 * @param {MarkerCluster} cluster
 * @return {DivIcon}
 */
const createClusterCustomIcon = (cluster) => {
  const childCount = cluster.getChildCount()
  return new Leaflet.DivIcon({
    html: `<div><span>+${childCount}</span></div>`,
    className: 'marker-cluster marker-cluster-large',
    iconSize: new Leaflet.Point(50, 50),
  })
}

/**
 * @typedef {Object} LeafletMapProps
 * @property {LeafletPlace[]} places
 * @property {[number, number]=} center
 * @property {number|null=} zoomLevel
 * @property {string=} selectedPlaceId
 * @property {Boolean} isLoading
 */

/**
 * Leaflet maps that use a Google Maps layer and display places markers.
 * @param {LeafletMapProps} props
 * @return {JSX.Element}
 */
const LeafletMap = (props) => {
  const { places, center, zoomLevel, selectedPlaceId, isLoading } = props
  const apiKey = getDrupalSetting('mapsApiKey') || `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
  const windowSize = useWindowResize()
  const isMobile = useMemo(() => windowSize.width <= 576, [windowSize.width])
  const [init, setInit] = useState(false)

  /**
   * Set gesture handling for gesture handling.
   * @return {null}
   */
  const GestureHandlingSetter = () => {
    const map = useMap()
    // @ts-ignore
    map.gestureHandling = true
    // @ts-ignore
    map.gestureHandling.enable()
    map.addHandler('gestureHandling', GestureHandling)
    setInit(false)
    return null
  }

  /**
   * @type {MapContainerProps}
   */
  const containerOptions = useMemo(
    () => ({
      maxZoom: 18,
      scrollWheelZoom: false,
      closePopupOnClick: false,
      dragging: !isMobile,
      tap: !isMobile,
      gestureHandling: true,
    }),
    [isMobile]
  )

  if (center) {
    containerOptions.center = center
  }

  if (zoomLevel) {
    containerOptions.zoom = zoomLevel
  } else {
    containerOptions.bounds = composePlacesBounds(places)
  }

  return (
    <Box className="results-map">
      {isLoading && <Loader />}
      {!isLoading && (
        <MapContainer className="markercluster-map" {...containerOptions}>
          <ReactLeafletGoogleLayer apiKey={apiKey} />
          {init && <GestureHandlingSetter />}
          <MarkerClusterGroup
            disableClusteringAtZoom={17}
            showCoverageOnHover={false}
            iconCreateFunction={createClusterCustomIcon}
          >
            <LeafletMapPlaces places={places} selectedPlaceId={selectedPlaceId} />
          </MarkerClusterGroup>
        </MapContainer>
      )}
    </Box>
  )
}

export default LeafletMap
